import i18next from 'i18next';
import { toast } from 'sonner';

import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useBackupStore } from '@/store/backup.store.js';

const t = (key, defaultValue, vars) => i18next.t(key, { defaultValue, ...vars });

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Extract "Currently running" body from `pbm status` text. */
export function parseCurrentlyRunning(stdout = '') {
    const text = String(stdout || '');
    const idx = text.search(/Currently running:/i);
    if (idx < 0) return null;
    const after = text.slice(idx).split('\n').slice(1);
    const lines = [];
    for (const line of after) {
        if (/^={3,}/.test(line.trim())) continue;
        if (/^(Backups|PITR|Cluster|FS\b)/i.test(line.trim())) break;
        if (!line.trim()) {
            if (lines.length) break;
            continue;
        }
        lines.push(line.trim());
    }
    const body = lines.join('\n').trim();
    if (!body || /^\(none\)$/i.test(body)) return null;
    return body;
}

export function isPbmAgentOk(stdout = '') {
    return /pbm-agent.*\bOK\b/i.test(String(stdout || ''));
}

export function isPbmAgentLost(stdout = '') {
    return /lost agent|pbm-agent\s+FAILED/i.test(String(stdout || ''));
}

/**
 * Heuristic: restore still in flight even if "Currently running" flickered empty.
 * Do not match snapshot `restore_to_time` labels — that caused permanent "busy" while agents were lost.
 */
export function looksLikeRestoreBusy(stdout = '', runningBody = null) {
    if (runningBody && /\brestore\b/i.test(runningBody) && !/restore_to_time/i.test(runningBody)) {
        return true;
    }
    if (runningBody && /physical restore|Starting restore|restore from/i.test(runningBody)) {
        return true;
    }
    // Agent lost alone is not enough (common for ~1–2 min after mongod restart).
    // Only treat as busy when the running section (or explicit restore op) is active.
    const running = runningBody || parseCurrentlyRunning(stdout);
    if (running && isPbmAgentLost(stdout)) return true;
    return false;
}

/**
 * Hard restore failures only — never match Cluster `FAILED status:` (agent heartbeat blip).
 */
export function hasHardRestoreFailure(stdout = '') {
    const text = String(stdout || '');
    const running = parseCurrentlyRunning(text) || '';
    const blob = `${running}\n${text}`;
    return (
        /no confirmation that restore has successfully started/i.test(blob)
        || /unable to run the restore/i.test(blob)
        || /check mongod binary/i.test(blob)
        || /\[restore\/[^\]]*\].*\berror\b/i.test(blob)
        || /\brestore:\s*(error|failed)\b/i.test(blob)
        || /Error:\s*.*restore/i.test(blob)
    );
}

/**
 * Map PBM status text → UI step key for restore progress.
 * @returns {'queued'|'coordinating'|'shutdown'|'copying'|'restarting'|'verifying'|null}
 */
export function inferRestoreStepKey({ running, stdout, phase, statusUnreachable } = {}) {
    if (statusUnreachable) return 'restarting';
    const blob = `${running || ''}\n${stdout || ''}`;
    if (phase === 'verifying' || phase === 'finishing') return 'verifying';
    if (/copy <|flushing old data|replacing|dbPath/i.test(blob)) return 'copying';
    if (/shutdown|stop agents|stopping mongod|waiting for the node/i.test(blob)) return 'shutdown';
    if (/waiting for|starting|converged|moving to state/i.test(blob)) return 'coordinating';
    if (/starting mongodb|restart|become reachable|lost agent/i.test(blob)) return 'restarting';
    if (running) return 'coordinating';
    return null;
}

function latestSnapshotHasError(stdout = '') {
    const lines = String(stdout).split('\n').map((l) => l.trim()).filter(Boolean);
    for (const line of lines) {
        if (/^\d{4}-\d{2}-\d{2}T/.test(line) && /\[ERROR:/i.test(line)) return true;
        if (/^\d{4}-\d{2}-\d{2}T/.test(line)) return false;
    }
    return false;
}

export class BackupIntegration {
    constructor(service) {
        this.service = service;
        this._pollToken = 0;
    }

    async fetchAll({ silent = false } = {}) {
        const store = useBackupStore.getState();
        if (!silent) {
            store.setLoading(true);
            store.clearError();
        }
        try {
            const response = await this.service.list();
            const data = response.data || {};
            store.setList({
                local: data.local || [],
                pbm: data.pbm || [],
                pbmRaw: data.pbmRaw || null,
                schedule: data.schedule || null,
            });
            return { success: true, data };
        } catch (error) {
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupLoadFailed', 'Could not load backups');
            store.setError(message);
            if (!silent) toast.error(message);
            return { success: false, error: message };
        } finally {
            if (!silent) store.setLoading(false);
        }
    }

    async fetchStatus({ silent = false } = {}) {
        const store = useBackupStore.getState();
        if (!silent) store.setStatusLoading(true);
        try {
            const response = await this.service.pbmStatus();
            const data = response.data || null;
            store.setStatus(data);
            if (data?.schedule && store.setSchedule) store.setSchedule(data.schedule);
            return { success: true, data };
        } catch (error) {
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupStatusFailed', 'Could not load backup status');
            if (!silent) toast.error(message);
            return { success: false, error: message };
        } finally {
            if (!silent) store.setStatusLoading(false);
        }
    }

    /**
     * Poll PBM status until idle (or timeout). Updates activeJob for the UI banner.
     * Restore waits longer: agent must be OK again after mongod restarts.
     */
    async _watchPbmJob({
        kind,
        label,
        targetName = null,
        timeoutMs = 45 * 60 * 1000,
        intervalMs = 4000,
    }) {
        const store = useBackupStore.getState();
        const token = ++this._pollToken;
        const startedAt = Date.now();
        const isRestore = kind === 'restore';
        // Restore: status often flickers empty while files still copy / mongod restarts
        const requiredIdle = isRestore ? 6 : 2;
        const requireAgentOk = isRestore;

        store.setActiveJob({
            kind,
            label,
            detail: null,
            startedAt,
            phase: 'starting',
            targetName,
            stepKey: isRestore ? 'queued' : null,
        });
        store.setLastJobResult(null);

        toast.message(
            t('admin:messages.backupJobAccepted', 'Job accepted — tracking progress…'),
        );

        let sawBusy = false;
        let idleStreak = 0;
        let lastStdout = '';
        let statusErrors = 0;

        await sleep(1500);

        while (Date.now() - startedAt < timeoutMs) {
            if (token !== this._pollToken) return { success: false, cancelled: true };

            const res = await this.fetchStatus({ silent: true });
            if (!res.success) {
                statusErrors += 1;
                idleStreak = 0;
                store.patchActiveJob({
                    phase: 'running',
                    stepKey: isRestore ? 'restarting' : null,
                    detail: t(
                        'admin:backups.job.statusUnreachable',
                        'Waiting for PBM / MongoDB to become reachable…',
                    ),
                });
                if (statusErrors > 60) break;
                await sleep(intervalMs);
                continue;
            }
            statusErrors = 0;
            lastStdout = res.data?.stdout || '';
            const running = parseCurrentlyRunning(lastStdout);
            const restoreBusy = isRestore && looksLikeRestoreBusy(lastStdout, running);
            const agentOk = isPbmAgentOk(lastStdout);
            const busyNow = Boolean(running) || restoreBusy || (isRestore && sawBusy && !agentOk);

            const stepKey = isRestore
                ? inferRestoreStepKey({
                    running,
                    stdout: lastStdout,
                    phase: busyNow ? 'running' : 'finishing',
                    statusUnreachable: false,
                })
                : null;

            if (busyNow) {
                sawBusy = true;
                idleStreak = 0;
                store.patchActiveJob({
                    phase: 'running',
                    stepKey: stepKey || (isRestore ? 'coordinating' : null),
                    detail: running
                        || (restoreBusy
                            ? t('admin:backups.job.restoreCopying', 'Copying backup files into the data directory…')
                            : !agentOk
                                ? t('admin:backups.job.restoreWaitingAgent', 'Waiting for MongoDB / PBM agent to come back…')
                                : null),
                });
            } else if (sawBusy) {
                idleStreak += 1;
                const verifying = requireAgentOk && !agentOk
                    ? false
                    : idleStreak >= Math.max(2, requiredIdle - 2);
                store.patchActiveJob({
                    phase: verifying ? 'verifying' : 'finishing',
                    stepKey: isRestore ? 'verifying' : null,
                    detail: t(
                        'admin:backups.job.finishing',
                        'Finishing — confirming PBM is idle and the agent is healthy…',
                    ),
                });
                if (requireAgentOk && !agentOk) {
                    idleStreak = 0;
                    store.patchActiveJob({
                        phase: 'running',
                        stepKey: 'restarting',
                        detail: t(
                            'admin:backups.job.restoreWaitingAgent',
                            'Waiting for MongoDB / PBM agent to come back…',
                        ),
                    });
                } else if (idleStreak >= requiredIdle) {
                    break;
                }
            } else if (Date.now() - startedAt > (isRestore ? 45_000 : 20_000)) {
                // Never saw busy — job may have finished very quickly (or failed to start)
                idleStreak += 1;
                if (idleStreak >= requiredIdle) break;
            }

            await sleep(intervalMs);
        }

        if (token !== this._pollToken) return { success: false, cancelled: true };

        store.patchActiveJob({
            phase: 'verifying',
            stepKey: isRestore ? 'verifying' : null,
            detail: t('admin:backups.job.finishing', 'Finishing — confirming PBM is idle…'),
        });
        await Promise.all([
            this.fetchAll({ silent: true }),
            this.fetchStatus({ silent: true }),
        ]);

        // Extra settle for restore: mongod/agent often need 1–2 min after physical copy
        if (isRestore) {
            const settleDeadline = Date.now() + 120_000;
            while (Date.now() < settleDeadline) {
                if (token !== this._pollToken) return { success: false, cancelled: true };
                await this.fetchStatus({ silent: true });
                const out = useBackupStore.getState().status?.stdout || '';
                const running = parseCurrentlyRunning(out);
                const still = Boolean(running) || looksLikeRestoreBusy(out, running);
                if (!still && isPbmAgentOk(out) && !hasHardRestoreFailure(out)) break;
                store.patchActiveJob({
                    phase: 'verifying',
                    stepKey: isPbmAgentOk(out) ? 'verifying' : 'restarting',
                    detail: isPbmAgentOk(out)
                        ? t(
                            'admin:backups.job.restoreSettling',
                            'Restore settling — waiting for a stable idle status…',
                        )
                        : t(
                            'admin:backups.job.restoreWaitingAgent',
                            'Waiting for MongoDB / PBM agent to come back…',
                        ),
                });
                await sleep(4000);
            }
            await this.fetchAll({ silent: true });
        }

        const finalStdout = useBackupStore.getState().status?.stdout || lastStdout;
        const runningFinal = parseCurrentlyRunning(finalStdout);
        const stillRunning = Boolean(runningFinal)
            || (isRestore && looksLikeRestoreBusy(finalStdout, runningFinal));
        const timedOut = Date.now() - startedAt >= timeoutMs && stillRunning;
        const snapshotError = kind === 'backup' && latestSnapshotHasError(finalStdout);
        const restoreError = isRestore && hasHardRestoreFailure(finalStdout);
        const agentOk = isPbmAgentOk(finalStdout);
        // Physical restore stops mongod → transient "lost agent". If the op went idle
        // without a hard error, treat data restore as success (warn if agent still down).
        const watchedLongEnough = Date.now() - startedAt >= 45_000;
        const restoreLikelyDone = isRestore
            && !stillRunning
            && !restoreError
            && (sawBusy || watchedLongEnough || agentOk);

        store.clearActiveJob();

        if (timedOut) {
            const message = t(
                'admin:messages.backupJobTimeout',
                'Job is still running after the watch timeout — check PBM status.',
            );
            store.setLastJobResult({ ok: false, kind, message, at: Date.now(), targetName });
            toast.error(message);
            return { success: false, error: message };
        }

        if (snapshotError || restoreError) {
            const message = t(
                'admin:messages.backupJobFailed',
                'Job finished with an error — see PBM status / snapshot list.',
            );
            store.setLastJobResult({ ok: false, kind, message, at: Date.now(), targetName });
            toast.error(message);
            return { success: false, error: message };
        }

        if (isRestore && !agentOk && !restoreLikelyDone) {
            const message = t(
                'admin:messages.backupRestoreAgentDown',
                'Restore finished but the PBM agent is not OK yet — check MongoDB and restart the agent if needed.',
            );
            store.setLastJobResult({ ok: false, kind, message, at: Date.now(), targetName });
            toast.error(message);
            return { success: false, error: message };
        }

        if (isRestore && targetName) {
            store.setDatabaseState({
                snapshotName: targetName,
                restoredAt: Date.now(),
                pitrTime: null,
            });
        }

        // Restore rewinds Mongo PBM metadata; storage still has status:done.
        // Controller resyncs too — call again here so the UI never sticks on
        // Failed / 0.00B for the restored-to snapshot.
        if (isRestore && (restoreLikelyDone || !stillRunning)) {
            try {
                await this.service.pbmResync();
            } catch {
                // Controller may already be resyncing; refresh list anyway.
            }
            await Promise.all([
                this.fetchAll({ silent: true }),
                this.fetchStatus({ silent: true }),
            ]);
        }

        if (isRestore && restoreLikelyDone && !agentOk) {
            const message = t(
                'admin:messages.backupRestoreCompletedAgentWarn',
                'Restore completed from {{name}}. PBM agent is still recovering — refresh status; take a new Full base when agent is OK.',
                { name: targetName || label },
            );
            store.setLastJobResult({
                ok: true,
                kind,
                message,
                at: Date.now(),
                targetName: targetName || null,
                agentRecovering: true,
            });
            toast.success(message);
            return { success: true, agentRecovering: true };
        }

        const message = isRestore
            ? t('admin:messages.backupRestoreCompletedNamed', 'Restore completed from {{name}}', {
                name: targetName || label,
            })
            : t('admin:messages.backupCompleted', 'Backup completed');
        store.setLastJobResult({
            ok: true,
            kind,
            message,
            at: Date.now(),
            targetName: targetName || null,
        });
        toast.success(message);
        return { success: true };
    }

    /** PBM physical backup: incremental | base | full */
    async run(type = 'incremental') {
        const store = useBackupStore.getState();
        if (store.activeJob) {
            toast.error(t('admin:messages.backupJobBusy', 'Another backup job is already running'));
            return { success: false, error: 'busy' };
        }
        store.setLoading(true);
        const label =
            type === 'base' || type === 'full'
                ? t('admin:backups.job.baseLabel', 'Full (base) backup')
                : t('admin:backups.job.incrLabel', 'Incremental backup');
        try {
            await this.service.pbmRun({ type });
            store.setLoading(false);
            return await this._watchPbmJob({ kind: 'backup', label });
        } catch (error) {
            store.clearActiveJob();
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupRunFailed', 'Backup failed to start');
            toast.error(message);
            store.setLastJobResult({ ok: false, kind: 'backup', message, at: Date.now() });
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /** Logical mongodump (awaits completion on API). */
    async runDump() {
        const store = useBackupStore.getState();
        if (store.activeJob) {
            toast.error(t('admin:messages.backupJobBusy', 'Another backup job is already running'));
            return { success: false, error: 'busy' };
        }
        const label = t('admin:backups.job.dumpLabel', 'Logical mongodump');
        store.setActiveJob({
            kind: 'dump',
            label,
            detail: t('admin:backups.job.dumpDetail', 'Writing archive to BACKUP_DIR…'),
            startedAt: Date.now(),
            phase: 'running',
            targetName: null,
            stepKey: null,
        });
        store.setLastJobResult(null);
        store.setLoading(true);
        toast.message(t('admin:messages.backupDumpStarted', 'Logical dump started'));
        try {
            const response = await this.service.run({ engine: 'mongodump', type: 'full' });
            await this.fetchAll({ silent: true });
            const message = t('admin:messages.backupDumpCompleted', 'Logical dump completed');
            store.setLastJobResult({ ok: true, kind: 'dump', message, at: Date.now() });
            toast.success(message);
            return { success: true, data: response.data };
        } catch (error) {
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupDumpFailed', 'Logical dump failed');
            store.setLastJobResult({ ok: false, kind: 'dump', message, at: Date.now() });
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.clearActiveJob();
            store.setLoading(false);
        }
    }

    async saveSchedule(payload) {
        const store = useBackupStore.getState();
        if (store.activeJob) {
            toast.error(t('admin:messages.backupJobBusy', 'Another backup job is already running'));
            return { success: false, error: 'busy' };
        }
        try {
            const response = await this.service.updateSchedule(payload);
            const data = response.data || payload;
            store.setSchedule({
                ...(store.schedule || {}),
                ...data,
                editable: data.editable !== false,
            });
            toast.success(
                t('admin:messages.backupScheduleUpdated', 'Backup schedule updated'),
            );
            if (response.warning) {
                toast.message(getIntegrationErrorMessage({ message: response.warning }));
            }
            return { success: true, data };
        } catch (error) {
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupScheduleUpdateFailed', 'Could not update schedule');
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async testStorage() {
        try {
            const response = await this.service.testStorage();
            const ok = response.data?.ok !== false;
            const message = ok
                ? t('admin:messages.backupStorageTestOk', 'Storage test succeeded')
                : getIntegrationErrorMessage({ message: response.data?.message })
                    || t('admin:messages.backupStorageTestFailed', 'Storage test failed');
            if (ok) toast.success(message);
            else toast.error(message);
            return { success: ok, data: response.data, error: ok ? null : message };
        } catch (error) {
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupStorageTestFailed', 'Storage test failed');
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async updateStorage(type) {
        const store = useBackupStore.getState();
        if (store.activeJob) {
            toast.error(t('admin:messages.backupJobBusy', 'Another backup job is already running'));
            return { success: false, error: 'busy' };
        }
        try {
            const response = await this.service.updateStorage({ type, confirm: true });
            const data = response.data || {};
            store.setSchedule({
                ...(store.schedule || {}),
                ...data,
                storageType: data.storageType || type,
                editable: data.editable !== false,
            });
            toast.success(
                t('admin:messages.backupStorageUpdated', 'Backup storage target updated'),
            );
            return { success: true, data };
        } catch (error) {
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupStorageUpdateFailed', 'Could not update storage target');
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async deleteSnapshot(name) {
        const store = useBackupStore.getState();
        if (store.activeJob) {
            toast.error(t('admin:messages.backupJobBusy', 'Another backup job is already running'));
            return { success: false, error: 'busy' };
        }
        try {
            await this.service.pbmDelete({ name });
            await this.fetchAll({ silent: true });
            toast.success(
                t('admin:messages.backupSnapshotDeleted', 'Snapshot deleted: {{name}}', { name }),
            );
            return { success: true };
        } catch (error) {
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupSnapshotDeleteFailed', 'Could not delete snapshot');
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Rebuild PBM catalog from storage. Use when a healthy snapshot shows as
     * Failed / 0.00B after physical restore (Mongo metadata rolled back).
     */
    async resyncCatalog() {
        const store = useBackupStore.getState();
        if (store.activeJob) {
            toast.error(t('admin:messages.backupJobBusy', 'Another backup job is already running'));
            return { success: false, error: 'busy' };
        }
        try {
            const response = await this.service.pbmResync();
            await Promise.all([
                this.fetchAll({ silent: true }),
                this.fetchStatus({ silent: true }),
            ]);
            toast.success(
                t(
                    'admin:messages.backupCatalogResynced',
                    'Backup catalog resynced from storage',
                ),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupCatalogResyncFailed', 'Could not resync backup catalog');
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async restore(name, { pitrTime, confirm = true } = {}) {
        const store = useBackupStore.getState();
        if (store.activeJob) {
            toast.error(t('admin:messages.backupJobBusy', 'Another backup job is already running'));
            return { success: false, error: 'busy' };
        }
        store.setLoading(true);
        const label = t('admin:backups.job.restoreLabel', 'Restore {{name}}', { name });
        try {
            await this.service.pbmRestore({ name, pitrTime, confirm });
            store.setLoading(false);
            return await this._watchPbmJob({
                kind: 'restore',
                label,
                targetName: name,
                timeoutMs: 90 * 60 * 1000,
                intervalMs: 5000,
            });
        } catch (error) {
            store.clearActiveJob();
            const message =
                getIntegrationErrorMessage(error)
                || t('admin:messages.backupRestoreFailed', 'Restore failed');
            toast.error(message);
            store.setLastJobResult({ ok: false, kind: 'restore', message, at: Date.now(), targetName: name });
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }
}
